import { useMemo } from 'react';

import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { useLocalStorage } from 'hooks/use-local-storage';
import moment from 'moment';
import { Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Brush,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Button } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

import './ReportingChart.css';

const GROUP_BY_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

const formatPeriodLabel = (value, groupBy) => {
  const m = moment(value, 'YYYY-MM-DD');
  if (!m.isValid()) return value;
  if (groupBy === 'month') return m.format('MMM YYYY');
  return m.format('MMM D');
};

const MAX_CHART_POINTS = 200;

const fillDateGaps = (data, groupBy, seriesKeys) => {
  if (!data || data.length < 2) return data;
  if (data.length > MAX_CHART_POINTS) return data;

  const dateFormat = 'YYYY-MM-DD';
  const start = moment(data[0].period, dateFormat);
  const end = moment(data[data.length - 1].period, dateFormat);

  const unitMap = { day: 'days', week: 'weeks', month: 'months' };
  const unit = unitMap[groupBy] || 'weeks';

  const existingMap = {};
  data.forEach(row => {
    const parsed = { period: row.period };
    seriesKeys.forEach(key => {
      parsed[key] = Number(row[key]) || 0;
    });
    existingMap[row.period] = parsed;
  });

  const zeroRow = {};
  seriesKeys.forEach(key => {
    zeroRow[key] = 0;
  });

  const filled = [];
  const cursor = start.clone();

  while (cursor.isSameOrBefore(end, 'day')) {
    const periodStr = cursor.format(dateFormat);
    filled.push(existingMap[periodStr] || { period: periodStr, total: 0, ...zeroRow });
    cursor.add(1, unit);
    if (filled.length > MAX_CHART_POINTS) return data;
  }

  return filled;
};

const CustomTooltip = ({ active, payload, label, groupBy }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-[rgba(45,38,77,0.9)] rounded-lg py-2 px-3 min-w-[140px]">
      <p className="text-sm font-semibold text-white m-0 mb-1">
        {formatPeriodLabel(label, groupBy)}
      </p>
      {payload.map(entry => (
        <p key={entry.name} className="flex items-center gap-1.5 m-0 text-sm text-white">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="flex-1 text-white">{entry.name}</span>
          <span className="font-semibold text-white">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

export const ReportingChart = ({
  data = [],
  series = [],
  groupBy = 'week',
  onGroupByChange,
  datePreset,
  datePresets = [],
  onDatePresetChange,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useLocalStorage('reporting_chart_expanded', true);

  const filledData = useMemo(() => {
    const seriesKeys = series.map(s => s.key);
    return fillDateGaps(data, groupBy, seriesKeys);
  }, [data, groupBy, series]);

  return (
    <div className="bg-white border border-brand-primary-5 p-[15px] rounded-3xl overflow-x-auto overflow-y-hidden max-w-full mb-6">
      <div className={cn('flex justify-between items-center pl-[5px]', expanded ? 'mb-4' : 'mb-0')}>
        <div className="flex items-center gap-1">
          <h3 className="text-2xl text-brand-primary-200 font-semibold m-0">Trends</h3>
          <IconComponent
            className={cn(
              'transition-transform duration-[250ms] ease-in-out cursor-pointer',
              expanded ? 'rotate-0' : '-rotate-90',
            )}
            icon={ArrowDown01Icon}
            onClick={() => setExpanded(!expanded)}
            noBackground
            type="secondary"
            size="large"
            strokeWidth={1.5}
          />
        </div>
        {expanded && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              {(datePresets || [])?.map(preset => (
                <Button
                  key={preset.key}
                  type={datePreset === preset.key ? 'primary' : 'secondary'}
                  small
                  prependIcon={false}
                  onClick={() => onDatePresetChange(preset.key)}>
                  {preset.label}
                </Button>
              ))}
            </div>
            <div
              className="w-px min-w-px h-10 mx-2 bg-brand-primary-5 self-center hidden md:!block"
              aria-hidden
            />
            <div className="flex items-center gap-2 shrink-0">
              {GROUP_BY_OPTIONS.map(option => (
                <Button
                  key={option.value}
                  type={groupBy === option.value ? 'primary' : 'secondary'}
                  small
                  prependIcon={false}
                  onClick={() => onGroupByChange(option.value)}>
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
      <Collapse in={expanded}>
        <div className="pb-2.5">
          {!filledData?.length ? (
            <div className="flex items-center justify-center h-[300px] text-sm text-gray-400">
              {t('common.noDataAvailable')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={filledData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  {series.map(s => (
                    <linearGradient
                      key={s.key}
                      id={`gradient-${s.key}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1">
                      <stop offset="5%" stopColor={s.color} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 12, fontFamily: 'Inter, sans-serif', fill: '#666' }}
                  tickFormatter={value => formatPeriodLabel(value, groupBy)}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fontFamily: 'Inter, sans-serif', fill: '#666' }}
                />
                <Tooltip content={<CustomTooltip groupBy={groupBy} />} />
                <Legend wrapperStyle={{ paddingTop: 16, paddingBottom: 16 }} />
                {series.map(s => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2}
                    fill={`url(#gradient-${s.key})`}
                  />
                ))}
                <Brush
                  dataKey="period"
                  height={20}
                  stroke="#e5dff2"
                  fill="#faf9fd"
                  travellerWidth={8}
                  tickFormatter={value => formatPeriodLabel(value, groupBy)}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Collapse>
    </div>
  );
};
